import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import ApiManager from '../../api/ApiManager'
import { HttpMethods } from '../../enums/HttpMethodsEnum'
import Loader from '../../components/common/Loader'
import RouteConstants from '../../constants/RouteConstants'
import StringConstants from '../../constants/StringConstants'
import UrlConstants from '../../constants/UrlConstants'
import cookie from 'react-cookies'
import currentPlanStore from '../../datastore/CurrentPlanStore'
import customerStore from '../../datastore/CustomerStore'
import mPartDPlansFilterStore from '../../datastore/medicareQuotes/MPartPlanFilterQuotesStore'
import medicareAdvantageFilterQuoteStore from '../../datastore/medicareQuotes/MedicareAdvantageFilterQuoteStore'
import msPlanFiltersStore from '../../datastore/medicareQuotes/MSupplementPlanFiltersStore'
import prescriptionStore from '../../datastore/PrescriptionStore'
import snackbarStore from '../../datastore/SnackbarStore'
import userActionStore from '../../datastore/UserActionStore'
import { deleteToken, isAuthenticatedUser } from '../../utils/AuthUtils'
import { PreferenceCoverageShortNameEnum } from '../../enums/PreferenceCoverageShortNameEnum'
import { SAMPLE_PRICE_BANNER_KEY } from '../../components/DashboardPage/SamplePriceBanner'
import { upsertPreLeadObject, getGoogleClientId } from '../../service/preLeadService'

const clearAllStores = () => {
  deleteToken()
  customerStore.reset()
  userActionStore.reset()
  snackbarStore.reset()
  prescriptionStore.reset()
  currentPlanStore.reset()
  medicareAdvantageFilterQuoteStore.clearStore()
  medicareAdvantageFilterQuoteStore.clearMAPlanRequestFilters()
  msPlanFiltersStore.clearMSPlanFilterStore()
  msPlanFiltersStore.clearMSPlanRequestFilters()
  mPartDPlansFilterStore.clearStore()
  mPartDPlansFilterStore.clearMPartDPlanRequestFilters()
  localStorage.clear()
  sessionStorage.clear()
}

const saveTokenAndFetchProfile = (
  token: string
): Promise<void> => {
  cookie.save(StringConstants.COOKIE_TOKEN, token, { path: RouteConstants.HOME })
  return ApiManager.makeApiCallWithAuthentication(
    UrlConstants.GET_CUSTOMER_DETAILS.USECASE,
    HttpMethods.GET_METHOD
  ).then((profileResponse) => {
    if (profileResponse?.status === 200) {
      customerStore.set(profileResponse.data.data)
      return
    }
    throw profileResponse
  })
}

const LitePage: React.FC = () => {
  const navigate = useNavigate()

  useEffect(() => {
    if (isAuthenticatedUser()) {
      navigate(RouteConstants.DASHBOARD, { replace: true })
      return
    }

    let active = true
    const params = new URLSearchParams(window.location.search)
    const campaignToken = params.get('campaigntoken')

    if (campaignToken) {
      // Campaign redirect flow: GET /api/v1/lite/session/campaign-redirect
      ApiManager.makeApiCall(
        UrlConstants.LITE_CAMPAIGN_REDIRECT.USECASE,
        HttpMethods.GET_METHOD,
        { campaigntoken: campaignToken }
      )
        .then((response) => {
          if (!active) return
          if (response?.status === 200) {
            const { token, redirectUrl } = response.data.data
            return saveTokenAndFetchProfile(token).then(() => {
              if (!active) return
              localStorage.setItem(SAMPLE_PRICE_BANNER_KEY, 'true')
              navigate(redirectUrl, { replace: true })
            })
          }
          throw response
        })
        .catch(() => {
          if (!active) return
          clearAllStores()
          navigate(RouteConstants.LOGIN, { replace: true })
        })
    } else {
      // Create session flow: POST /api/v1/lite/session with all query params as body
      const body: Record<string, string | boolean> = {}
      params.forEach((value, key) => {
        if (value !== '' && value !== undefined && value !== null) body[key] = value
      })

      // Coerce boolean fields per API schema
      const BOOLEAN_FIELDS = ['smsOptIn', 'spouseOptIn', 'excludeFromBoberdooAPIs']
      BOOLEAN_FIELDS.forEach((field) => {
        if (field in body) {
          body[field] = body[field] === 'true'
        }
      })

      if (process.env.REACT_APP_QT_VARIANT_ID) {
        body.qtVariantId = process.env.REACT_APP_QT_VARIANT_ID
      }

      // Dummy last name required by backend for lite session creation.
      // Never surface this in the frontend - profile inputs blank it for lite/lite_identified status.
      body.lastName = 'UPDATE'

      ApiManager.makeApiCall(
        UrlConstants.CREATE_LITE_SESSION.USECASE,
        HttpMethods.POST_METHOD,
        body
      )
        .then((response) => {
          if (!active) return
          if (response?.status === 200) {
            const { token } = response.data.data
            return saveTokenAndFetchProfile(token).then(() => {
              if (!active) return
              const allParams: Record<string, unknown> = {}
              params.forEach((value, key) => { if (value) allParams[key] = value })
              upsertPreLeadObject({
                ...allParams,
                leadClientIDGoogle: allParams.leadClientIDGoogle || getGoogleClientId(),
                ...(customerStore.email && { email: customerStore.email }),
                ...(customerStore.phone && { phone: customerStore.phone }),
                ...(customerStore.postalCode && { postalCode: customerStore.postalCode }),
                ...(customerStore.gender && { gender: customerStore.gender }),
                ...(customerStore.dateOfBirth && { dob: customerStore.dateOfBirth }),
                ...(customerStore.firstName && { firstName: customerStore.firstName }),
                ...(customerStore.lastName && { lastName: customerStore.lastName }),
              })
              localStorage.setItem(SAMPLE_PRICE_BANNER_KEY, 'true')
              // preferenceCoverage is the canonical field name; pc is the legacy shorthand
              const pc = params.get('preferenceCoverage') ?? params.get('pc')
              let dashboardURL = RouteConstants.DASHBOARD
              if (pc === PreferenceCoverageShortNameEnum.ma)
                dashboardURL = RouteConstants.DASHBOARD_MA
              else if (pc === PreferenceCoverageShortNameEnum.ms)
                dashboardURL = RouteConstants.DASHBOARD_MS
              else if (pc === PreferenceCoverageShortNameEnum.pdp)
                dashboardURL = RouteConstants.DASHBOARD_PDP
              navigate(dashboardURL, { replace: true })
            })
          }
          throw response
        })
        .catch(() => {
          if (!active) return
          clearAllStores()
          navigate(RouteConstants.LOGIN, { replace: true })
        })
    }

    return () => {
      active = false
    }
  }, [navigate])

  return (
    <Loader
      message={StringConstants.AUTO_LOGIN_LOADER_MESSAGE}
      contents={StringConstants.AUTO_LOGIN_LOADER_CAROUSEL_CONTENT}
      leadBrand={customerStore.brandName ?? ''}
      pageName='quoting_tool_lite_loader_screen'
    />
  )
}

export default LitePage
